import { useCallback, useEffect, useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { usePaperTheme } from '@/hooks/usePaperTheme';
import { Button } from '@/components/ui/Button';
import { UsersPanel } from '@/features/admin/components/UsersPanel';
import { MetricsPanel } from '@/features/admin/components/MetricsPanel';
import { AuditPanel } from '@/features/admin/components/AuditPanel';
import { FeedbackPanel } from '@/features/admin/components/FeedbackPanel';

type AdminTab = 'invites' | 'users' | 'metrics' | 'audit' | 'feedback';
import {
  approveInviteRequest,
  createInvite,
  declineInviteRequest,
  deleteInvite,
  listInviteRequests,
  listInvites,
} from '@/services/api/auth';
import type { InviteCode, InviteRequest, Plan } from '@/types/auth';
import toast from 'react-hot-toast';

const PLAN_OPTIONS: { value: Plan; label: string; description: string }[] = [
  { value: 'free', label: 'Free', description: '10/day' },
  { value: 'pro', label: 'Pro', description: '200/day' },
  { value: 'byo', label: 'BYO', description: '1000/day' },
];

const PLAN_BADGE: Record<Plan, string> = {
  free: 'bg-ink-muted/15 text-ink-muted',
  pro: 'bg-brand-500/15 text-brand-400',
  byo: 'bg-emerald-500/15 text-emerald-300',
};

function timeAgo(ts: string): string {
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function AdminPage() {
  usePaperTheme();
  const [tab, setTab] = useState<AdminTab>('invites');
  const [invites, setInvites] = useState<InviteCode[]>([]);
  const [requests, setRequests] = useState<InviteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [email, setEmail] = useState('');
  const [plan, setPlan] = useState<Plan>('free');

  const load = useCallback(async () => {
    try {
      const [inviteData, requestData] = await Promise.all([
        listInvites(),
        listInviteRequests('pending').catch(() => [] as InviteRequest[]),
      ]);
      setInvites(inviteData);
      setRequests(requestData);
    } catch {
      toast.error('Failed to load invites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (req: InviteRequest, approvePlan: Plan) => {
    try {
      const result = await approveInviteRequest(req.id, approvePlan);
      setInvites((prev) => [result.invite, ...prev]);
      setRequests((prev) => prev.filter((r) => r.id !== req.id));
      toast.success(result.email_sent ? `Invite sent to ${req.email}` : 'Invite created');
    } catch {
      toast.error('Failed to approve request');
    }
  };

  const handleDecline = async (req: InviteRequest) => {
    try {
      await declineInviteRequest(req.id);
      setRequests((prev) => prev.filter((r) => r.id !== req.id));
      toast.success('Request declined');
    } catch {
      toast.error('Failed to decline request');
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const result = await createInvite(email.trim() || undefined, plan);
      setInvites((prev) => [result.invite, ...prev]);
      if (result.email_sent) {
        toast.success(`Invite sent to ${email}`);
      } else if (email.trim()) {
        toast.success('Invite created (email not configured)');
      } else {
        toast.success('Invite created');
      }
      setEmail('');
      setPlan('free');
    } catch {
      toast.error('Failed to create invite');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteInvite(id);
      setInvites((prev) => prev.filter((i) => i.id !== id));
      toast.success('Invite deleted');
    } catch {
      toast.error('Failed to delete invite');
    }
  };

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('Copied to clipboard');
  };

  return (
    <AppLayout>
      <main className="flex-1 overflow-y-auto px-6 py-8">
        <div className="mx-auto max-w-4xl">
          <PageHeader
            title="Admin"
            description="Manage invites, users, and see activation metrics."
            backTo="/dashboard"
          />

          <div className="mb-6 flex gap-1 border-b border-line-subtle [body.paper_&]:border-stone-subtle">
            {(['invites', 'users', 'metrics', 'audit', 'feedback'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`px-3 py-2 text-tiny font-medium capitalize transition-colors ${
                  tab === t
                    ? 'border-b-2 border-brand-500 text-ink-primary [body.paper_&]:border-teal-500 [body.paper_&]:text-charcoal'
                    : 'text-ink-muted hover:text-ink-secondary [body.paper_&]:text-charcoal-muted [body.paper_&]:hover:text-charcoal-soft'
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {tab === 'users' && <UsersPanel />}
          {tab === 'metrics' && <MetricsPanel />}
          {tab === 'audit' && <AuditPanel />}
          {tab === 'feedback' && <FeedbackPanel />}
          {tab === 'invites' && <InvitesContent
            invites={invites} requests={requests} loading={loading}
            creating={creating} email={email} plan={plan}
            setEmail={setEmail} setPlan={setPlan}
            onCreate={handleCreate} onDelete={handleDelete} onCopy={handleCopy}
            onApprove={handleApprove} onDecline={handleDecline}
          />}
        </div>
      </main>
    </AppLayout>
  );
}

// Extract existing invites UI into its own component to fit the tab layout.
// Props are passed through from AdminPage so state stays up there.
interface InvitesProps {
  invites: InviteCode[];
  requests: InviteRequest[];
  loading: boolean;
  creating: boolean;
  email: string;
  plan: Plan;
  setEmail: (v: string) => void;
  setPlan: (v: Plan) => void;
  onCreate: () => void;
  onDelete: (id: string) => void;
  onCopy: (code: string) => void;
  onApprove: (req: InviteRequest, plan: Plan) => void;
  onDecline: (req: InviteRequest) => void;
}

function InvitesContent({
  invites, requests, loading, creating, email, plan,
  setEmail, setPlan, onCreate, onDelete, onCopy, onApprove, onDecline,
}: InvitesProps) {
  return (
    <>
          {/* Pending requests inbox */}
          {requests.length > 0 && (
            <div className="mt-6">
              <h2 className="text-sm font-semibold text-ink-secondary">
                Pending requests <span className="ml-1 text-ink-faint">({requests.length})</span>
              </h2>
              <div className="mt-2 space-y-2">
                {requests.map((req) => (
                  <RequestRow key={req.id} req={req} onApprove={onApprove} onDecline={onDecline} />
                ))}
              </div>
            </div>
          )}

          {/* Create invite with optional email + plan */}
          <div className="mt-6 rounded-clyra-lg border border-line-default bg-clyra-raised p-4">
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-1.5">
                <label className="block text-petit font-medium text-ink-secondary">
                  Recipient email <span className="text-ink-faint">(optional)</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  className="w-full rounded-clyra border border-line-default bg-clyra-bg px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted transition-colors focus:border-brand-500/40 focus:outline-none focus:ring-1 focus:ring-brand-500/20"
                  onKeyDown={(e) => { if (e.key === 'Enter') onCreate(); }}
                />
              </div>
              <Button onClick={onCreate} loading={creating}>
                {email.trim() ? 'Send Invite' : 'Generate Code'}
              </Button>
            </div>

            {/* Plan selector */}
            <div className="mt-3 space-y-1.5">
              <label className="block text-petit font-medium text-ink-secondary">
                Plan <span className="text-ink-faint">(applied on signup)</span>
              </label>
              <div className="flex rounded-clyra border border-line-default bg-clyra-bg/50">
                {PLAN_OPTIONS.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPlan(p.value)}
                    className={`flex-1 px-3 py-1.5 text-tiny font-medium transition-all first:rounded-l-clyra last:rounded-r-clyra ${
                      plan === p.value
                        ? 'bg-brand-500/12 text-brand-400'
                        : 'text-ink-secondary hover:text-ink-primary'
                    }`}
                  >
                    {p.label}
                    <span className="ml-1 text-micro text-ink-faint">{p.description}</span>
                  </button>
                ))}
              </div>
            </div>

            <p className="mt-2 text-micro text-ink-faint">
              {email.trim()
                ? 'An email will be sent with the invite code and a direct signup link.'
                : 'A code will be generated. You can copy and share it manually.'}
            </p>
          </div>

          {/* Invite list */}
          {loading ? (
            <div className="mt-16 text-center text-sm text-ink-muted">Loading...</div>
          ) : invites.length === 0 ? (
            <div className="mt-8 rounded-clyra-lg border border-dashed border-line-default p-12 text-center">
              <p className="text-sm text-ink-secondary">No invites yet</p>
              <p className="mt-1 text-petit text-ink-muted">Generate one to invite a beta tester</p>
            </div>
          ) : (
            <div className="mt-4 space-y-2">
              {invites.map((inv) => (
                <div
                  key={inv.id}
                  className={`flex items-center justify-between rounded-clyra border p-3 transition-colors ${
                    inv.used_by
                      ? 'border-line-subtle bg-clyra-base'
                      : 'border-line-default bg-clyra-raised'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <code
                      className={`rounded-md px-2.5 py-1 font-mono text-sm ${
                        inv.used_by
                          ? 'bg-clyra-overlay/50 text-ink-faint line-through'
                          : 'bg-brand-500/8 text-brand-400 cursor-pointer hover:bg-brand-500/12 transition-colors'
                      }`}
                      onClick={() => !inv.used_by && onCopy(inv.code)}
                      title={inv.used_by ? 'Used' : 'Click to copy'}
                    >
                      {inv.code}
                    </code>
                    <span
                      className={`rounded-full px-2 py-0.5 text-micro font-medium uppercase tracking-wider ${PLAN_BADGE[inv.plan] ?? PLAN_BADGE.free}`}
                    >
                      {inv.plan}
                    </span>
                    <span className="text-tiny text-ink-faint">
                      {inv.used_by ? (
                        <>Used {inv.used_at ? timeAgo(inv.used_at) : ''}</>
                      ) : (
                        <>Created {timeAgo(inv.created_at)}</>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {inv.used_by ? (
                      <span className="rounded-full bg-clyra-overlay px-2 py-0.5 text-micro text-ink-faint">
                        used
                      </span>
                    ) : (
                      <>
                        <button
                          onClick={() => onCopy(inv.code)}
                          className="text-tiny text-ink-muted hover:text-ink-secondary transition-colors"
                        >
                          Copy
                        </button>
                        <button
                          onClick={() => onDelete(inv.id)}
                          className="text-tiny text-danger/60 hover:text-danger transition-colors"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
    </>
  );
}

interface RequestRowProps {
  req: InviteRequest;
  onApprove: (req: InviteRequest, plan: Plan) => void;
  onDecline: (req: InviteRequest) => void;
}

function RequestRow({ req, onApprove, onDecline }: RequestRowProps) {
  const [plan, setPlan] = useState<Plan>('free');
  return (
    <div className="rounded-clyra border border-line-default bg-clyra-raised p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink-primary truncate">{req.email}</p>
          <p className="mt-0.5 text-micro text-ink-faint">
            {timeAgo(req.created_at)}
          </p>
          {req.note && (
            <p className="mt-2 text-tiny text-ink-secondary whitespace-pre-wrap">{req.note}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <select
            value={plan}
            onChange={(e) => setPlan(e.target.value as Plan)}
            className="rounded-clyra border border-line-default bg-clyra-bg/50 px-2 py-1 text-tiny text-ink-primary focus:border-brand-400/40 focus:outline-none"
          >
            <option value="free">Free</option>
            <option value="pro">Pro</option>
            <option value="byo">BYO</option>
          </select>
          <button
            onClick={() => onApprove(req, plan)}
            className="rounded-clyra bg-brand-500/15 px-2.5 py-1 text-tiny font-medium text-brand-400 hover:bg-brand-500/25 transition-colors"
          >
            Approve
          </button>
          <button
            onClick={() => onDecline(req)}
            className="text-tiny text-danger/70 hover:text-danger transition-colors"
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  );
}
